import { FormEvent, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Mail, ShieldCheck } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Logo } from "../../components/Logo";
import { api, ApiError } from "../../lib/api";
import { useSession } from "../../lib/session";

function phoneDigits(value: string) {
  const digits = value.replace(/\D/g, "");
  if (digits.length > 10 && digits.startsWith("91")) {
    return digits.slice(2, 12);
  }
  return digits.slice(0, 10);
}

function normalizeMobile(value: string) {
  return `+91${phoneDigits(value)}`;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const session = useSession();
  const [mobileNumber, setMobileNumber] = useState("");
  const [otpRequestId, setOtpRequestId] = useState<string | null>(null);
  const [otp, setOtp] = useState("");

  const sendOtpMutation = useMutation({
    mutationFn: () => api.sendOtp(normalizeMobile(mobileNumber)),
    onSuccess(result) {
      setOtpRequestId(result.otp_request_id);
    }
  });

  const verifyOtpMutation = useMutation({
    mutationFn: () => api.verifyOtp(otpRequestId ?? "", normalizeMobile(mobileNumber), otp.trim()),
    onSuccess(result) {
      queryClient.clear();
      session.saveSession({ accessToken: result.access_token, refreshToken: result.refresh_token });
      const from = typeof location.state === "object" && location.state && "from" in location.state
        ? String(location.state.from)
        : "/";
      navigate(from, { replace: true });
    }
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (otpRequestId) {
      verifyOtpMutation.mutate();
    } else {
      sendOtpMutation.mutate();
    }
  }

  const error = sendOtpMutation.error ?? verifyOtpMutation.error;
  const pending = sendOtpMutation.isPending || verifyOtpMutation.isPending;

  return (
    <main className="pa-login-page">
      <section className="pa-login-card">
        <Logo />
        <div className="pa-login-icon">
          {otpRequestId ? <ShieldCheck size={30} /> : <Mail size={30} />}
        </div>
        <h1>{otpRequestId ? "Verify operator login" : "Platform admin sign in"}</h1>
        <p>{otpRequestId ? "Enter the OTP sent for Coprint sign in." : "Use your Coprint OTP login to access Tech Operations."}</p>
        <form onSubmit={submit} className="pa-login-form">
          <label>
            Phone number
            <div className="pa-phone-input-row">
              <span>+91</span>
              <input
                value={mobileNumber}
                onChange={(event) => setMobileNumber(phoneDigits(event.target.value))}
                placeholder="98765 43210"
                inputMode="numeric"
                minLength={10}
                maxLength={10}
                disabled={Boolean(otpRequestId)}
                required
              />
            </div>
          </label>
          {otpRequestId ? (
            <label>
              OTP
              <input
                value={otp}
                onChange={(event) => setOtp(event.target.value)}
                inputMode="numeric"
                minLength={4}
                maxLength={8}
                placeholder="123456"
                required
              />
            </label>
          ) : null}
          {error ? (
            <div className="pa-form-error">
              {error instanceof ApiError ? error.message : "Something went wrong."}
            </div>
          ) : null}
          <button className="pa-primary-button full" disabled={pending}>
            {otpRequestId ? "Verify" : "Send OTP"}
          </button>
          {otpRequestId ? (
            <button type="button" className="pa-link-button" onClick={() => setOtpRequestId(null)}>
              Change mobile number
            </button>
          ) : null}
        </form>
      </section>
    </main>
  );
}
